import React, { useState } from "react";
import { Box, Button, Checkbox, Collapse, FormControlLabel, TextField } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { PhoneFieldWithDeliveryCheck } from "../../orders/DeliveryRecordCheck";
import { OrderFormData } from "./orderFormSchema";

interface Props {
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    /** Open "More details" from the start (e.g. a prefill already filled them). */
    detailsOpen?: boolean;
}

/**
 * A new customer in as few fields as possible: name and (optional) phone —
 * walk-in customers may have none. Their address is the order's delivery
 * address. Everything else waits under "More details".
 */
export const NewCustomerFields: React.FC<Props> = ({ control, errors, detailsOpen = false }) => {
    const [more, setMore] = useState(detailsOpen);
    const err = errors.new_customer;

    const text = (
        name: "last_name" | "email" | "date_of_birth" | "profile" | "map_link" | "note",
        label: string,
        extra: Record<string, unknown> = {},
    ) => (
        <Controller
            name={`new_customer.${name}`}
            control={control}
            render={({ field }) => (
                <TextField
                    {...field}
                    value={field.value ?? ""}
                    fullWidth
                    size="small"
                    label={label}
                    error={!!err?.[name]}
                    helperText={err?.[name]?.message}
                    {...extra}
                />
            )}
        />
    );

    return (
        <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                <Controller
                    name="new_customer.first_name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            value={field.value ?? ""}
                            fullWidth
                            size="small"
                            label="First name"
                            required
                            autoFocus
                            error={!!err?.first_name}
                            helperText={err?.first_name?.message}
                        />
                    )}
                />
                {text("last_name", "Last name")}
            </Box>
            <Controller
                name="new_customer.phone"
                control={control}
                render={({ field }) => (
                    <PhoneFieldWithDeliveryCheck
                        {...field}
                        value={field.value ?? ""}
                        fullWidth
                        size="small"
                        label="Phone (optional)"
                        error={!!err?.phone}
                        helperText={err?.phone?.message}
                    />
                )}
            />

            <Box>
                <Button
                    size="small"
                    onClick={() => setMore((v) => !v)}
                    endIcon={more ? <ExpandLess /> : <ExpandMore />}
                    sx={{ px: 0.5 }}
                >
                    More details
                </Button>
                <Collapse in={more} unmountOnExit>
                    <Box sx={{ display: "grid", gap: 1.5, pt: 1 }}>
                        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                            {text("email", "Email", { type: "email" })}
                            <Controller
                                name="new_customer.alternative_phone"
                                control={control}
                                render={({ field }) => (
                                    <PhoneFieldWithDeliveryCheck
                                        {...field}
                                        value={field.value ?? ""}
                                        fullWidth
                                        size="small"
                                        label="Alternative phone"
                                        error={!!err?.alternative_phone}
                                        helperText={err?.alternative_phone?.message}
                                    />
                                )}
                            />
                        </Box>
                        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                            {text("date_of_birth", "Date of birth", { type: "date", InputLabelProps: { shrink: true } })}
                            {text("map_link", "Map link", { placeholder: "https://maps.google.com/..." })}
                        </Box>
                        {text("profile", "Profile", {
                            multiline: true,
                            minRows: 2,
                            placeholder: "Facebook or other profile links, anything useful",
                        })}
                        {text("note", "Note about the customer", { multiline: true, minRows: 2 })}
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Controller
                                name="new_customer.marketing_consent"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                        label="Marketing consent"
                                    />
                                )}
                            />
                            <Controller
                                name="new_customer.newsletter_subscription"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                        label="Newsletter"
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
};

export default NewCustomerFields;
